<template>
  <div class="datatable-content">
      <table class="table" :class="[customClass, $mq]">
          <thead class="headers">
              <tr>
                  <th v-if="header.visible" v-for="(header, header_index) in headers" class="column" @click="sort(header.value)" :class="[{ 'col-icon': header.value == 'icon' }, { sortedDesc: columnSortedDesc == header.value }, { sortedAsc: columnSortedDesc && columnSortedAsc == header.value }, header.value, { 'date-column': header.value === 'date' && ![$mq].includes('portrait') }]" :key="header_index">
                      {{ header.text }}
                  </th>
              </tr>
          </thead>

          <tbody class="table-values scrollbar">
              <tr class="values" v-for="(tableItem, index) in tableItems" :key="index" @click="emitAddEdit(tableItem.id)" v-if="!tableItem.hide">
                  <td
                      v-for="(headerValue, idx) in headers"
                      class="column-values"
                      v-show="headerValue.visible"
                      :data-type="tableItems[index][headerValue.value + '_type']"
                      :data-value="tableItem[headerValue.value]"
                      :class="[
                          { odd: index % 2 === 0 },
                          { even: index % 2 !== 0 },
                          {
                              draft: tableItem.statusB === 1 || (typeof tableItem.statusB === 'undefined' && tableItem.status === 0)
                          },
                          { 'sync-state': tableItem.requireSync },
                          headerValue.value
                      ]"
                      :key="idx"
                  >
                      <!-- PHOTO, GALLERY OR SIGNATURE / FOTO, GALERÍA O FIRMA -->
                      <template v-if="(tableItems[index][headerValue.value + '_type'] === FORMAT.PHOTO || tableItems[index][headerValue.value + '_type'] === FORMAT.SIGNATURE) && tableItem[headerValue.value]">
                          <div class="column-values">
                              <!-- handle object with multiple images -->
                              <template v-if="typeof tableItem[headerValue.value] === 'object'">
                                  <img v-for="(img, imgIndex) in Object.values(tableItem[headerValue.value])" :key="imgIndex" :src="img" :loading="isBase64(img) ? 'lazy' : 'eager'" />
                              </template>
                              <!-- handle single image -->
                              <template v-else>
                                  <img :src="tableItem[headerValue.value]" :loading="isBase64(tableItem[headerValue.value]) ? 'lazy' : 'eager'" />
                              </template>
                          </div>
                      </template>

                      <!-- If data-type is not 5, display the regular content using v-html -->
                      <!-- STATUS COLUMN CONDITION -->
                      <template v-if="tool === 'Records' && headerValue && headerValue.value === 'status'">
                          <span v-if="tableItem[headerValue.value] === 1" class="record-status completed">
                              <span class="color-dot completed"></span>
                              <span class="text">{{ $t('register.tabs.completed').toUpperCase() }}</span>
                          </span>
                          <span v-else class="record-status draft">
                              <span class="color-dot draft"></span>
                              <span class="text">{{ $t('register.tabs.draft').toUpperCase() }}</span>
                          </span>
                      </template>

                      <!-- TYPE SELECTOR -->
                      <template v-else-if="tableItems[index][headerValue.value + '_type'] === 11">
                          <div v-if="tableItem[headerValue.value] && tableItem[headerValue.value + '_config']['multiselect'] && convertStringToArray(tableItem[headerValue.value]).length > 0">
                              <div v-if="tableItem[headerValue.value]" v-for="option in convertStringToArray(tableItem[headerValue.value])" :key="option" class="selector-container">
                                  <span
                                      v-if="getMultipleValues(tableItem[headerValue.value + '_config'], option)"
                                      class="selector-color"
                                      :style="{
                                          background: getMultipleValues(tableItem[headerValue.value + '_config'], option).c
                                      }"
                                      :class="[{ 'no-color': !getMultipleValues(tableItem[headerValue.value + '_config'], option).c }]"
                                  ></span>
                                  <span class="selector-item">
                                      {{ option }}
                                  </span>
                              </div>
                          </div>
                          <div v-else class="selector-container">
                              <span
                                  v-if="getMultipleValues(tableItem[headerValue.value + '_config'], tableItem[headerValue.value])"
                                  class="selector-color"
                                  :style="{
                                      background: getMultipleValues(tableItem[headerValue.value + '_config'], tableItem[headerValue.value]).c
                                  }"
                                  :class="[
                                      {
                                          'no-color': !getMultipleValues(tableItem[headerValue.value + '_config'], tableItem[headerValue.value]).c
                                      }
                                  ]"
                              ></span>
                              <span class="selector-item">
                                  {{ tableItem[headerValue.value] }}
                              </span>
                          </div>
                      </template>

                      <!-- ACTION PLANS TYPE -->
                      <template v-else-if="tableItems[index][headerValue.value + '_type'] === 'actionPlans'">
                          <ActionPlanQuantity
                              :percentage="tableItem.actionPlans.percentage"
                              :open="tableItem.actionPlans.open"
                              :in-progress="tableItem.actionPlans.in_progress"
                              :completed="tableItem.actionPlans.completed"
                              :others="tableItem.actionPlans.others"
                          />
                      </template>

                      <!-- STATUS TYPE (AUDIT) -->
                      <template v-else-if="headerValue.type === 'status' || tableItems[index][headerValue.value + '_type'] === 'status'">
                          <!-- En portrait mode, la columna 'auditory' muestra status y score juntos -->
                          <template v-if="headerValue.value === 'auditory' && tableItem.auditoryScore">
                              <div class="audit-info">
                                  <ColorAndTextTag
                                      v-if="tableItem[headerValue.value + 'Data']"
                                      :circleColor="tableItem[headerValue.value + 'Data'].color"
                                      :text="tableItem[headerValue.value + 'Data'].name.toUpperCase()"
                                  />
                                  <span v-html="tableItem.auditoryScore"></span>
                              </div>
                          </template>
                          <!-- Para otras columnas de status, solo mostrar el tag -->
                          <template v-else>
                              <ColorAndTextTag
                                  v-if="tableItem[headerValue.value + 'Data']"
                                  :circleColor="tableItem[headerValue.value + 'Data'].color"
                                  :text="tableItem[headerValue.value + 'Data'].name.toUpperCase()"
                              />
                              <span v-else class="status-placeholder"> - </span>
                          </template>
                      </template>

                      <!-- DEFAULT CONTENT DISPLAY -->
                      <template v-else>
                          <span v-if="shouldRenderAsHtml(index, headerValue.value)" v-html="tableItem[headerValue.value]"></span>
                      </template>
                  </td>
              </tr>
          </tbody>
      </table>
  </div>
</template>

<script>
import { FORMAT } from '@/constants'
import ActionPlanStatus from './StatusTag.vue'
import ActionPlanQuantity from './StatusQuantity.vue'
import ColorAndTextTag from './ColorAndTextTag.vue'

export default {
  name: 'Datatable',
  components: {
      ActionPlanStatus,
      ActionPlanQuantity,
      ColorAndTextTag
  },
  props: {
      headers: {
          type: undefined
      },
      tableItems: {
          type: Array
      },
      customClass: {
          type: undefined,
          default: false
      },
      tool: {
          type: String,
          default: undefined
      }
  },
  data() {
      return {
          columnSortedDesc: false,
          columnSortedAsc: false,
          FORMAT: FORMAT
      }
  },
  methods: {
      convertStringToArray(value) {
          return value.split('|')
      },
      getMultipleValues(config, value) {
          return config.values.find(({ v }) => v === value)
      },
      sort(header) {
          if (this.tool === 'Records' && header === 'status') {
              return
          }

          if (!this.columnSortedDesc && !this.columnSortedAsc) {
              this.columnSortedDesc = header
          } else if (this.columnSortedDesc && this.columnSortedDesc != header) {
              this.columnSortedDesc = header
              this.columnSortedAsc = false
          } else if (this.columnSortedDesc && !this.columnSortedAsc && this.columnSortedDesc == header) {
              this.columnSortedAsc = header
          } else {
              this.columnSortedDesc = false
              this.columnSortedAsc = false
          }

          this.$emit('sort', { columnSortedDesc: this.columnSortedDesc, columnSortedAsc: this.columnSortedAsc })
      },
      isBase64(str) {
          const base64Regex = /^data:image\/[^;]+;base64[^"]+/i
          return base64Regex.test(str)
      },
      emitAddEdit(id) {
          this.$emit('emitAddEdit', id)
      },
      shouldRenderAsHtml(index, headerValue) {
          const dataType = this.tableItems[index][headerValue + '_type']

          // Formatos que NO deben mostrar el HTML con estilos que llega por prop (lo que se genera en el método fillTable de resume.vue)
          const excludedFormats = [this.FORMAT.CHECK, this.FORMAT.YESNO, this.FORMAT.YESNONC, this.FORMAT.PHOTO, this.FORMAT.SIGNATURE]

          return !excludedFormats.includes(dataType)
      }
  },
  mounted() {}
}
</script>

<style lang="scss" scoped>
.headers {
  tr {
      th.column {
          font-size: 12px;

          &.status {
              width: 140px;
              max-width: 140px;
          }
      }
  }
}

.selector-container {
  width: 100%;
  height: 100%;
  // padding: 8px;

  position: relative;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  @include border-radius(3px);
  @include font-size(0.875rem);
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;

  .selector-color {
      padding: 5px;
      @include border-radius(100%);
      // margin-left: 8px;

      &.no-color {
          // border: 1px solid $color-selector2;
          position: relative;
          background-image: url('/img/noColor.svg');
          background-size: 11px;
          background-position: center;
          background-repeat: no-repeat;
          width: 11px;
          height: 11px;
      }
  }

  &.multiselect {
      padding-bottom: 0px;
  }

  .selector-item {
      width: 100%;
      max-width: 200px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      text-align: left;
  }
}

.status-placeholder {
  color: $color-black;
}

#content.register {
  .table {
      .table-values {
          .column-values {
              &.odd {
                  background-color: white !important;
              }
              &.even {
                  background-color: #f9fafb !important;
              }

              &.draft {
                  &.odd {
                      background-color: white !important;
                  }
                  &.even {
                      background-color: #f9fafb !important;
                  }
              }

              .record-status {
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  border-radius: 4px;
                  border: 1px solid #e2e2e2;
                  background: white;
                  gap: 8px;
                  width: max-content;

                  .color-dot {
                      width: 10px;
                      height: 10px;
                      border-radius: 50%;

                      &.completed {
                          background-color: $color-success-500;
                      }
                      &.draft {
                          background-color: $color-warning-500;
                      }
                  }

                  .text {
                      font-size: 14px;
                  }
              }
          }
      }
  }
}
</style>

<style lang="scss">
.audit {
  @include border-radius(5px);
  width: calc(100%);
  // margin-right: $alex-app-padding;
  overflow: auto;
  float: right;
  height: calc(100% - 65px);
  .headers {
      tr {
          th.column {
              // text-align: left;
              @include text-ellipsis();
              max-width: 200px !important;
          }
      }
  }
  .table-values {
      .values {
          .column-values {
              // text-align: left;

              .main-img {
                  height: 30px !important;
              }

              // EMPLOYEE
              .user-color {
                  @include display-flex();
                  @include align-items(center);
                  width: calc(100% - 10px);
                  height: 100%;
                  border-radius: unset;
                  top: -2px;
                  position: relative;
                  .main-img {
                      width: 30px;
                      height: 30px;
                      min-width: 30px;
                      min-height: 30px;
                  }
                  .name {
                      @include font-size($size: s, $important: true);
                      @include text-ellipsis();
                      height: fit-content;
                      padding-left: 4px;
                      overflow: visible;
                      position: relative;
                      width: 100%;
                      top: 0px;
                  }
              }
              // STATUS
              .status {
                  @include font-size(12px);
                  padding: 4px 4px;
                  border-radius: 3px;
                  position: relative;
                  top: -3px;
                  font-family: $text-bold;

                  &.completed,
                  &.progress,
                  &.scheduled,
                  &.tostart {
                      display: inline-flex;
                      align-items: baseline;
                      justify-content: center;
                      min-width: 110px;
                      padding: 0 8px;
                  }

                  &.completed {
                      background-color: $color-success-100;
                      color: $color-success-800;

                      &::before {
                          content: '';
                          display: inline-block;
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          background-color: $color-success-500;
                          margin-right: 4px;
                      }
                  }
                  &.progress {
                      // background-color: $color-info-500;
                      background-color: $color-warning-100;
                      color: $warning-dark;
                      &::before {
                          content: '';
                          display: inline-block;
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          background-color: $warning-dark;
                          margin-right: 4px;
                      }
                  }
                  &.scheduled {
                      background-color: $color-neutral-500;
                      color: white;
                  }
                  &.tostart {
                      background-color: $color-neutral-500;
                      color: white;
                  }
              }
          }
      }
  }
  &.portrait {
      .table {
          min-width: 100% !important;
          width: min-content;

          .headers {
              tr {
                  th.column {
                      @include font-size(xs);
                      max-width: 200px;
                      min-width: 120px;
                      padding-top: 8px;
                      // padding-left: 0px;

                      &.simpleDate {
                          min-width: 50px;
                      }

                      &.auditory {
                          min-width: 180px;
                      }
                  }
              }
          }

          .table-values {
              .values {
                  // min-height: 75px;
                  height: 75px;

                  .column-values {
                      width: 30px;
                      min-width: 50px;

                      &.simpleDate {
                          font-family: $text-bold;
                      }

                      &.auditory {
                          @include background($image: img('right_neutro.svg'), $size: 12px, $position: right 10px center);
                          padding: 10px 10px 10px 12px;
                          line-height: 10px;
                          min-height: 75px;

                          // STATUS
                          .status {
                              @include font-size(12px);
                              // padding: 4px 5px;
                              padding: 0 8px;
                              border-radius: 3px;
                              position: relative;
                              top: -3px;
                              text-transform: uppercase;
                              //font-family: $text-light;
                              font-family: $text-bold;

                              &.completed {
                                  background-color: $color-success-100;
                                  color: $color-success-800;
                              }
                              &.progress {
                                  // background-color: $color-info-500;
                                  background-color: $color-warning-100;
                                  color: $warning-dark;
                              }
                              &.scheduled {
                                  background-color: $info;
                                  color: $main;
                              }
                          }

                          // SCORE
                          .total {
                              @include font-size($size: m, $important: true);
                              font-family: $text-bold;
                              color: $neutro-s80;
                              margin-left: 10px;
                          }

                          // EMPLOYEE
                          .user-color {
                              @include display-flex();
                              @include align-items(center);
                              width: calc(100% - 10px);
                              height: 100%;
                              padding-top: 17px;
                              .main-img {
                                  width: 30px;
                                  height: 30px;
                                  min-width: 30px;
                                  min-height: 30px;
                              }
                              .name {
                                  @include font-size($size: s, $important: true);
                                  @include text-ellipsis();
                                  overflow: visible;
                                  height: 12px;
                                  padding-left: 4px;
                                  width: 100%;
                              }
                          }
                      }
                  }
              }
          }
      }
  }
                            //   line-height: 10px;
}
</style>
